import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Loader2, ExternalLink, Download, ArrowLeft, Users, Sparkles, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { adminApi } from '../lib/api';

// Handful of top country picks + Global default. Kept short intentionally
// — admin can type anything else into the plain input if we ever extend.
const COUNTRY_OPTIONS = [
  'Global', 'India', 'United States', 'United Kingdom', 'Singapore',
  'United Arab Emirates', 'Canada', 'Australia', 'Germany', 'France',
];

function AdminDiscoverPageInner() {
  const [country, setCountry] = useState('Global');
  const [industry, setIndustry] = useState('');
  const [functionRole, setFunctionRole] = useState('');
  const [keyword, setKeyword] = useState('');
  const [minFollowers, setMinFollowers] = useState(6000);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [deepDiveBusy, setDeepDiveBusy] = useState(false);
  const [deepDiveProgress, setDeepDiveProgress] = useState(null); // {done, total}

  const runDeepDive = async (runId, peerIndices) => {
    if (deepDiveBusy || !runId || !peerIndices?.length) return;
    setDeepDiveBusy(true);
    setDeepDiveProgress({ done: 0, total: peerIndices.length });
    // Poll progress in parallel with the sync call so admins see live counters
    // (endpoint updates ``deep_dive_progress`` after each peer).
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await adminApi.discoveryRun(runId);
        if (r?.deep_dive_progress) setDeepDiveProgress(r.deep_dive_progress);
      } catch { /* ignore */ }
      if (!cancelled) setTimeout(poll, 4000);
    };
    setTimeout(poll, 3000);
    try {
      const { data } = await import('../lib/api').then((m) => m.api.post(
        `/admin/discover/runs/${runId}/deep-dive`,
        { peer_indices: peerIndices, max_posts_per_peer: 5 },
      ));
      cancelled = true;
      const okCount = (data?.deep_dives || []).filter((d) => d.status === 'complete').length;
      const total = data?.requested || peerIndices.length;
      if (okCount === total) {
        toast.success(`Deep-dive complete — ${okCount}/${total} peers analyzed`);
      } else {
        toast.warning(`${okCount}/${total} peers analyzed — check the deck for details`);
      }
      // Refresh the visible run so the deep-dives + gap synthesis appear.
      const fresh = await adminApi.discoveryRun(runId);
      setResult({ ...(fresh || {}), run_id: runId });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Deep-dive failed');
    } finally {
      cancelled = true;
      setDeepDiveBusy(false);
      setDeepDiveProgress(null);
    }
  };

  useEffect(() => {
    let alive = true;
    adminApi.discoveryRuns().then((r) => { if (alive) setRecent(r?.items || []); }).catch(() => {});
    return () => { alive = false; };
  }, [result]);

  const canRun = industry.trim().length > 1 && !running;

  const onRun = async (e) => {
    e?.preventDefault?.();
    if (!canRun) return;
    setRunning(true);
    setResult(null);
    const t = toast.loading('Searching Perplexity for peers + topics — up to 3 minutes…');
    try {
      const res = await adminApi.discover({
        country,
        industry: industry.trim(),
        function: functionRole.trim(),
        keyword: keyword.trim(),
        topics_count: 10,
        peers_count: 20,
        min_followers: minFollowers,
      });
      setResult(res);
      const errs = (res?.errors || []).length;
      if (errs) {
        toast.warning(`Search complete with ${errs} partial issue(s). See results below.`, { id: t, duration: 5000 });
      } else {
        toast.success(`Found ${res?.topics?.length || 0} topics & ${res?.peers?.length || 0} peers.`, { id: t });
      }
    } catch (err) {
      toast.error(err?.message || 'Discovery failed', { id: t });
    } finally {
      setRunning(false);
    }
  };

  const topics = result?.topics || [];
  const peers = result?.peers || [];

  const downloadDeck = async (runId) => {
    if (!runId) return;
    const t = toast.loading('Building deck…');
    try {
      const token = localStorage.getItem('le_token');
      const res = await fetch(
        `${(process.env.REACT_APP_BACKEND_URL || '') + '/api'}/admin/discover/runs/${runId}/deck`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `blayzrr-discovery-${(industry || 'run').replace(/\s+/g, '-').toLowerCase()}.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Deck downloaded.', { id: t });
    } catch (err) {
      toast.error(err?.message || 'Deck download failed', { id: t });
    }
  };

  const downloadCsv = (rows, filename, headers) => {
    if (!rows?.length) return;
    const esc = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const lines = [headers.map((h) => esc(h.label)).join(',')];
    rows.forEach((row) => {
      lines.push(headers.map((h) => esc(row[h.key])).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft size={12} /> Back to admin
          </Link>
          <h1 className="text-3xl font-semibold mt-2">Discover peers &amp; topics</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Country + industry + function + keyword search. Returns the top 10 trending topics for the
            filter and the top 20 real LinkedIn peers/influencers to study, backed by live-web search.
          </p>
        </div>
      </div>

      <Card className="border-border">
        <CardContent className="p-5">
          <form onSubmit={onRun} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 items-end">
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="discover-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="discover-industry">Industry *</Label>
              <Input
                id="discover-industry"
                data-testid="discover-industry"
                placeholder="e.g. SaaS, Hospitality, Fintech"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="discover-function">Function / role</Label>
              <Input
                id="discover-function"
                data-testid="discover-function"
                placeholder="e.g. Founder, Head of Growth"
                value={functionRole}
                onChange={(e) => setFunctionRole(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="discover-keyword">Keyword focus</Label>
              <Input
                id="discover-keyword"
                data-testid="discover-keyword"
                placeholder="e.g. AI adoption, PLG"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </div>
            <div className="col-span-full space-y-1.5">
              <Label>Peer follower threshold</Label>
              <div className="flex items-center gap-2 flex-wrap">
                {[5000, 10000, 25000, 50000].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setMinFollowers(n)}
                    data-testid={`discover-min-followers-${n}`}
                    className={`h-8 px-3 rounded-full text-xs font-mono-edge border transition-colors ${
                      minFollowers === n
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-transparent text-muted-foreground border-border hover:border-primary/40'
                    }`}
                  >
                    {n >= 1000 ? `${(n / 1000).toFixed(0)}K+` : `${n}+`} followers
                  </button>
                ))}
                <span className="text-[10.5px] text-muted-foreground ml-1">
                  Tighter threshold = fewer, higher-signal peers.
                </span>
              </div>
            </div>
            <div className="col-span-full">
              <Button type="submit" disabled={!canRun} className="rounded-full" data-testid="discover-run-btn">
                {running ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Search size={14} className="mr-2" />}
                Run discovery
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {result?.errors?.length ? (
        <Card className="border-amber-500/40 bg-amber-500/[0.04]">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle size={16} className="text-amber-500 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Partial results</p>
              <p className="text-muted-foreground mt-0.5">
                {(result.errors || []).map((e) => e).join(' · ')}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {result ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            Slice: <span className="text-foreground font-medium">
              {[industry, functionRole, country !== 'Global' ? country : '', keyword].filter(Boolean).join(' \u00b7 ') || 'Global'}
            </span>
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-full gap-1.5"
              onClick={() => runDeepDive(result.run_id, (result.peers || []).slice(0, 10).map((_, i) => i))}
              disabled={deepDiveBusy || !(result.peers || []).length}
              data-testid="discover-deep-dive-btn"
            >
              {deepDiveBusy
                ? <Loader2 size={13} className="animate-spin" />
                : <Sparkles size={13} />}
              {deepDiveProgress
                ? `Deep-diving ${deepDiveProgress.done}/${deepDiveProgress.total}\u2026`
                : `Deep-dive top ${Math.min(10, (result.peers || []).length)} peers`}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full gap-1.5"
              data-testid="discover-download-deck-btn"
              onClick={() => downloadDeck(result.run_id)}
            >
              <Download size={13} />
              {(result.deep_dives || []).some((d) => d?.status === 'complete')
                ? 'Download deep-dive deck'
                : 'Download PPT deck'}
            </Button>
          </div>
        </div>
      ) : null}

      {result ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Topics column */}
          <Card className="border-border" data-testid="discover-topics-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-mono-edge tracking-[0.14em] uppercase text-primary">
                    Top {topics.length} trending topics
                  </h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadCsv(
                    topics,
                    `topics-${industry.replace(/\s+/g, '-').toLowerCase()}.csv`,
                    [{key:'title',label:'title'},{key:'angle',label:'angle'},{key:'format_suggestion',label:'format'},{key:'why_trending',label:'why_trending'},{key:'engagement_level',label:'engagement'}],
                  )}
                  className="h-8 gap-1 text-[11.5px]"
                  data-testid="discover-topics-csv-btn"
                  disabled={!topics.length}
                >
                  <Download size={12} /> CSV
                </Button>
              </div>
              {topics.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No topics returned.</p>
              ) : (
                <ol className="space-y-3">
                  {topics.map((t, idx) => (
                    <li key={`${t.title}-${idx}`} className="text-sm border border-border rounded-md p-3" data-testid={`discover-topic-${idx}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="font-medium leading-snug">{idx + 1}. {t.title}</p>
                        {t.engagement_level ? (
                          <span className="text-[10px] font-mono-edge px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0">
                            {t.engagement_level}
                          </span>
                        ) : null}
                      </div>
                      {t.angle ? <p className="text-xs text-muted-foreground mt-1">Angle: {t.angle}</p> : null}
                      {t.why_trending ? <p className="text-xs text-muted-foreground mt-1">Why: {t.why_trending}</p> : null}
                      {t.articles?.length ? (
                        <div className="mt-2 border-t border-border pt-2 space-y-1.5">
                          <p className="text-[10px] font-mono-edge tracking-wide uppercase text-muted-foreground">
                            Top ranking articles
                          </p>
                          {t.articles.map((a, aidx) => (
                            <div key={`${a.url}-${aidx}`} className="text-[11px]">
                              <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline break-all">
                                {a.title || a.url}
                              </a>
                              {a.outlet ? <span className="text-muted-foreground"> · {a.outlet}</span> : null}
                              {a.ranking_keywords?.length ? (
                                <div className="flex gap-1 flex-wrap mt-0.5">
                                  {a.ranking_keywords.slice(0, 4).map((k) => (
                                    <span key={k} className="text-[9.5px] px-1 py-0.5 rounded bg-secondary text-muted-foreground">
                                      {k}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>

          {/* Peers column */}
          <Card className="border-border" data-testid="discover-peers-card">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-mono-edge tracking-[0.14em] uppercase text-primary">
                    Top {peers.length} peers &amp; influencers
                  </h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    {result.peers_provider ? (
                      <p className="text-[10px] text-muted-foreground">via {result.peers_provider}</p>
                    ) : null}
                    {result.peers_min_followers ? (
                      <p className="text-[10px] text-muted-foreground">
                        · min {(result.peers_min_followers / 1000).toFixed(0)}K followers
                      </p>
                    ) : null}
                    {result.peers_dropped_low_followers ? (
                      <p className="text-[10px] text-amber-600">
                        · {result.peers_dropped_low_followers} skipped &lt; threshold
                      </p>
                    ) : null}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => downloadCsv(
                    peers,
                    `peers-${industry.replace(/\s+/g, '-').toLowerCase()}.csv`,
                    [{key:'name',label:'name'},{key:'headline',label:'headline'},{key:'linkedin_url',label:'linkedin_url'},{key:'signal',label:'signal'},{key:'why',label:'why'}],
                  )}
                  className="h-8 gap-1 text-[11.5px]"
                  data-testid="discover-peers-csv-btn"
                  disabled={!peers.length}
                >
                  <Download size={12} /> CSV
                </Button>
              </div>
              {peers.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">No peers returned.</p>
              ) : (
                <ol className="space-y-2">
                  {peers.map((p, idx) => (
                    <li key={`${p.linkedin_url || p.name || idx}-${idx}`} className="text-sm border border-border rounded-md p-3" data-testid={`discover-peer-${idx}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <p className="font-medium leading-snug">{idx + 1}. {p.name || <span className="italic text-muted-foreground">(from URL)</span>}</p>
                        {p.signal ? (
                          <span className="text-[10px] font-mono-edge px-1.5 py-0.5 rounded bg-secondary text-muted-foreground shrink-0 capitalize">
                            {p.signal.replace(/_/g, ' ')}
                          </span>
                        ) : null}
                      </div>
                      {p.follower_count ? (
                        <p className="text-[11px] text-emerald-600 mt-0.5 font-mono-edge">
                          {(p.follower_count >= 1000
                            ? (p.follower_count >= 1000000
                              ? (p.follower_count / 1000000).toFixed(1) + 'M'
                              : (p.follower_count / 1000).toFixed(0) + 'K')
                            : p.follower_count)} followers
                        </p>
                      ) : p.follower_unknown ? (
                        <p className="text-[11px] text-amber-600 mt-0.5 font-mono-edge">
                          followers unknown · verify on LinkedIn
                        </p>
                      ) : null}
                      {p.headline ? <p className="text-xs text-muted-foreground mt-1">{p.headline}</p> : null}
                      {p.rationale ? (
                        <p className="text-xs mt-1"><span className="font-medium text-primary">Rationale:</span> <span className="text-muted-foreground">{p.rationale}</span></p>
                      ) : (p.why ? <p className="text-xs text-muted-foreground mt-1">Why: {p.why}</p> : null)}
                      {p.matched_on?.length ? (
                        <div className="flex gap-1 flex-wrap mt-1.5">
                          {p.matched_on.map((m) => (
                            <span key={m} className="text-[10px] font-mono-edge px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                              matched: {m}
                            </span>
                          ))}
                        </div>
                      ) : null}
                      {p.linkedin_url ? (
                        <a
                          href={p.linkedin_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
                        >
                          Open profile <ExternalLink size={10} />
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        !running && (
          <Card className="border-dashed border-border">
            <CardContent className="p-8 text-center">
              <Sparkles size={22} className="mx-auto text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground mt-3">
                Fill in the filter above and hit &quot;Run discovery&quot; to see 10 trending
                topics and 20 peer profiles matching your slice.
              </p>
            </CardContent>
          </Card>
        )
      )}

      {result && (result.deep_dives || result.deep_dive_synthesis) ? (
        <Card className="border-primary/30 bg-primary/[0.02]" data-testid="discover-deep-dive-panel">
          <CardContent className="p-5 space-y-5">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-sm font-mono-edge tracking-[0.14em] uppercase text-primary">
                  Deep-dive analysis
                </h3>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Apify-scraped posts + LLM content-gap synthesis. Deck includes these details.
                </p>
              </div>
              {result.deep_dive_completed_at && (
                <span className="text-[10px] text-muted-foreground font-mono-edge">
                  completed {new Date(result.deep_dive_completed_at).toLocaleString()}
                </span>
              )}
            </div>

            {(result.deep_dive_synthesis || []).length > 0 && (
              <div className="rounded-lg border border-primary/20 bg-white p-3">
                <div className="text-[10px] font-mono-edge tracking-[0.14em] uppercase text-primary/70 mb-1.5">
                  Cross-profile whitespace patterns
                </div>
                <ul className="space-y-1.5">
                  {result.deep_dive_synthesis.map((s, i) => (
                    <li key={i} className="text-[12.5px] leading-relaxed">
                      <span className="font-semibold">{s.pattern}</span>
                      {s.peer_count ? (
                        <span className="ml-1 text-[10px] font-mono-edge bg-amber-500/10 text-amber-700 px-1.5 py-0.5 rounded">
                          {s.peer_count} peers
                        </span>
                      ) : null}
                      {s.why_high_value ? <span className="block text-[11px] text-muted-foreground mt-0.5">{s.why_high_value}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(result.deep_dives || []).map((d, i) => (
                <div
                  key={i}
                  className={[
                    'rounded-lg border p-3 space-y-2',
                    d.status === 'complete'
                      ? 'border-primary/20 bg-white'
                      : 'border-red-300/50 bg-red-50/60',
                  ].join(' ')}
                  data-testid={`discover-deep-dive-${d.name || i}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-semibold">{d.name}</span>
                    <span className={[
                      'text-[9px] font-mono-edge px-1.5 py-0.5 rounded uppercase',
                      d.status === 'complete' ? 'bg-emerald-500/15 text-emerald-700' : 'bg-red-500/15 text-red-700',
                    ].join(' ')}>
                      {d.status}
                    </span>
                  </div>
                  {d.error ? <p className="text-[11px] text-red-600">{d.error}</p> : null}
                  {(d.content_focus || []).length > 0 && (
                    <div>
                      <div className="text-[9px] font-mono-edge uppercase text-muted-foreground mb-1">Focus themes</div>
                      <div className="flex flex-wrap gap-1">
                        {d.content_focus.map((f, j) => (
                          <span key={j} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded" title={f.one_liner || ''}>
                            {f.theme}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(d.content_gaps || []).length > 0 && (
                    <div>
                      <div className="text-[9px] font-mono-edge uppercase text-amber-700 mb-1">Whitespace opportunities</div>
                      <ul className="space-y-1">
                        {d.content_gaps.map((g, j) => (
                          <li key={j} className="text-[11px] leading-snug">
                            <span className="font-medium">{g.gap}</span>
                            {g.why_whitespace ? <span className="block text-[10px] text-muted-foreground">{g.why_whitespace}</span> : null}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(d.posts || []).length > 0 && (
                    <p className="text-[10px] text-muted-foreground">
                      Based on {d.posts.length} recent post{d.posts.length === 1 ? '' : 's'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {recent?.length > 0 && (
        <Card className="border-border" data-testid="discover-recent-card">
          <CardContent className="p-5">
            <h3 className="text-sm font-mono-edge tracking-[0.14em] uppercase text-muted-foreground mb-3">
              <Users size={12} className="inline mr-1.5" /> Recent discovery runs
            </h3>
            <ul className="divide-y divide-border">
              {recent.slice(0, 10).map((r) => (
                <li key={r.id} className="py-2 text-xs flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {r.filters?.industry}
                      {r.filters?.function ? ` · ${r.filters.function}` : ''}
                      {r.filters?.keyword ? ` · ${r.filters.keyword}` : ''}
                      {r.filters?.country && r.filters.country !== 'Global' ? ` · ${r.filters.country}` : ''}
                    </p>
                    <p className="text-muted-foreground mt-0.5">{new Date(r.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.errors?.length ? (
                      <span className="text-amber-500 text-[10px]">{r.errors.length} partial</span>
                    ) : null}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 gap-1 text-[11px]"
                      onClick={() => downloadDeck(r.id)}
                      data-testid={`discover-recent-deck-${r.id}`}
                    >
                      <Download size={11} /> Deck
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function AdminDiscoverPage() {
  return <AdminDiscoverPageInner />;
}
